import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Service } from '@mews-commander/data-access';
import { LocalizedText, lt, useLocalizeProperty } from '@mews/framework';
import { Banner, Stack, Typography } from '@optimus-web/core';
import { FloatingPanel, TreeOption } from '@mews/b2b-ui';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { TreeField } from '@mews-commander/form';
import { ItemsAndConditionsFormFields, ProductsFormFields, RoutingRuleFormValues } from '../../../../../schemas';
import { useCreateDeletedItemsCountText } from '../../../hooks';
import { getItemsDialogFooter } from './utils';

interface ProductsDialogProps {
    active: boolean;
    onClose: () => void;
    service: Service;
    productOptions: TreeOption[];
    selectedProductsCount: number;
    totalProductsCount: number;
    productsFieldName: string;
    initialSelectedProducts: string[];
}

export const ProductsDialog = ({
    active,
    onClose,
    service,
    productOptions,
    selectedProductsCount,
    totalProductsCount,
    productsFieldName,
    initialSelectedProducts,
}: ProductsDialogProps) => {
    const { setValue } = useFormContext();
    const localizeProperty = useLocalizeProperty();
    const deletedItemsText = useCreateDeletedItemsCountText(productOptions);
    const serviceName = localizeProperty(service.names);
    const selectedProductsFieldName = `${productsFieldName}.${ProductsFormFields.SelectedProducts}`;
    const {
        formState: { errors },
        trigger,
        clearErrors,
    } = useFormContext<RoutingRuleFormValues>();
    const errorKey = productsFieldName.includes(ItemsAndConditionsFormFields.AdditionalServices)
        ? ItemsAndConditionsFormFields.AdditionalServices
        : ItemsAndConditionsFormFields.Services;

    const handleDialogSave = useCallback(async () => {
        const isValid = await trigger(`${productsFieldName}.${ProductsFormFields.Type}` as keyof RoutingRuleFormValues);
        if (!isValid) {
            return;
        }
        clearErrors();
        onClose();
    }, [trigger, productsFieldName, clearErrors, onClose]);

    const handleDialogClose = useCallback(async () => {
        setValue(selectedProductsFieldName, initialSelectedProducts);
        const isValid = await trigger(`${productsFieldName}.${ProductsFormFields.Type}` as keyof RoutingRuleFormValues);
        if (isValid) {
            clearErrors();
        }
        onClose();
    }, [
        setValue,
        selectedProductsFieldName,
        initialSelectedProducts,
        trigger,
        productsFieldName,
        onClose,
        clearErrors,
    ]);

    return (
        <FloatingPanel
            active={active}
            onClose={handleDialogClose}
            title={lt(TextKey.RoutingRulesFormProductsDialogTitle)}
            footer={getItemsDialogFooter({
                handleDialogSave,
                handleDialogClose,
                dataTestIds: {
                    save: DataTestId.RoutingRulesProductsDialogSave,
                    cancel: DataTestId.RoutingRulesProductsDialogCancel,
                },
            })}
        >
            <Stack vertical spacing="200">
                <Typography variant="secondary" textStyle="bodyMedium" as="div">
                    <LocalizedText
                        textKey={TextKey.RoutingRulesFormProductsDialogDescription}
                        substitutes={{ service: serviceName }}
                    />
                </Typography>
                {errors?.itemsAndConditions?.[errorKey]?.[service.id]?.serviceProducts?.type && (
                    <Banner
                        variant="error"
                        primaryText={
                            <LocalizedText
                                textKey={
                                    // @ts-expect-error Property textKey does not exist on type
                                    errors?.itemsAndConditions?.[errorKey]?.[service.id]?.serviceProducts?.type?.textKey
                                }
                            />
                        }
                    />
                )}
                <TreeField
                    name={selectedProductsFieldName}
                    multiple
                    options={productOptions}
                    defaultExpandAll
                    showSelectAllToggle
                    showSearchBar
                    title={
                        <Typography variant="primary" textStyle="bodyMedium" as="div">
                            {selectedProductsCount}
                            {UnicodeEntity.ForwardSlash}
                            {totalProductsCount}
                            {UnicodeEntity.NoBreakSpace}
                            <LocalizedText textKey={TextKey.RoutingRulesFormProductsTreeProductSelected} />
                            {UnicodeEntity.NoBreakSpace}
                            {deletedItemsText}
                        </Typography>
                    }
                />
            </Stack>
        </FloatingPanel>
    );
};
